import React, {ReactNode, useMemo} from "react";
import {FlagIC, IstitutoContrattualeRecord} from "@commons/mapping/IstitutoContrattualeRecord";
import {Periodo} from "@commons/types";
import {IstitutiContrParams, RiepilogoICParams} from "@commons/services/presenze_api/riepilogoIC.api";
import {useIstitutiContrattuali, useRiepilogoIC} from "@commons/services/presenze_api/riepilogoIC.queries";
import {SaldoICRecord} from "@commons/mapping/SaldoICRecord";
import {PageType} from "../dashboardWF/wf.dashboard.detail";
import {DateTime} from "luxon";

export interface RiepilogoICCompParams {
    idAziendaMaster: number | undefined,
    idAziendaTabelle: number | undefined,
    idDip: number | undefined,
    periodo: Periodo,
    page?: PageType,
    containerClass?: string,
    tabellaAziendaNonConfigurata?: JSX.Element
}

const RiepilogoIC: React.FC<RiepilogoICCompParams> = ({
                                                          idAziendaMaster,
                                                          idAziendaTabelle,
                                                          idDip,
                                                          periodo,
                                                          page,
                                                          containerClass = '',
                                                          tabellaAziendaNonConfigurata
                                                      }) => {

    const params = useMemo(() => {
        if (idAziendaTabelle)
            return {
                idAziendaTabelle
            } as IstitutiContrParams;
        else return null;
    }, [idAziendaTabelle,]);
    const {data: dataIC, isLoading: isLoadingIC} = useIstitutiContrattuali(params);
    const tabellaAziendaConfigurata: boolean = (dataIC && dataIC.length > 0) || false;

    const riepilogoParams = useMemo(() => {
        if (tabellaAziendaConfigurata && idAziendaMaster && idDip && periodo)
            return {
                idAziendaMaster: idAziendaMaster,
                idDip: idDip,
                anno: periodo.anno,
                mese: periodo.mese,
            } as RiepilogoICParams;
        else return null;
    }, [tabellaAziendaConfigurata, idAziendaMaster, idDip, periodo,]);

    const {data: dataRiepilogo, isLoading: isLoadingRiepilogo} = useRiepilogoIC(riepilogoParams);

    if (!tabellaAziendaConfigurata) return <>{tabellaAziendaNonConfigurata}</>;

    const mostraValoreIC = function (idIC: string, posIC: number, campo: string) {
        if (dataIC && dataRiepilogo) {
            //const tipoCampo = dataIC!.map((item) => IstitutoContrattualeRecord.getFieldList(item))
            const tipoCampo = IstitutoContrattualeRecord.getFieldList(dataIC[posIC]);
            const ICGestito = dataIC[posIC].id === idIC;
            const valoreGestito = tipoCampo.find((el) => {
                return el === campo
            });

            return (ICGestito && valoreGestito !== undefined);
        } else
            return false;
    }

    const showSaldiAttualizzati = function (fn: (saldo: SaldoICRecord) => boolean, item: FlagIC | null) {
        let attualizzato = false;
        if (dataRiepilogo) {
            for (let ind0 = 0; ind0 < dataRiepilogo.length; ++ind0) {
                let saldo = dataRiepilogo[ind0];
                if (fn(saldo)) {
                    attualizzato = true;
                    break;
                }
            }
        }
        if (attualizzato && item) {
            // N.B. per i valori attualizzati fa fede solo l'impostazione delle ferie
            attualizzato = mostraValoreIC('FR', 0, item);
        }
        return attualizzato;
    }

    function showRow(campo: string) {
        if (dataIC) {
            const tipoCampo = dataIC!.map((item) => IstitutoContrattualeRecord.getFieldList(item))
            /*
            Cerco all'interno di TipoCampo, se c'è almeno un elemento che corrisponde a "ic".
                Quindi restituisco true se ne trovo almeno 1, e mostro la riga della tabella.
                Altrimenti false e non mostro la riga.
            */
            for (let item of tipoCampo) {
                if (item.some((arrVal) => campo === arrVal))
                    return true;
            }

        } else return false;
    }


    const numColTable = dataIC ? dataIC.length : null;

    const border = page === 'monitorWF' ? '' : 'border-[#218a9e]';
    const font = page === 'monitorWF' ? '' : 'fontTableRiepiloghiCaption';

    return <>
        <table className="tableRiepiloghi fontTableRiepiloghi">
            <caption className={`tableRiepiloghiCaption ${font}`}>
                Riepilogo ferie e permessi
            </caption>
            {(dataRiepilogo && dataIC) && <thead>
            <tr className={`border-b ${border}`}>
                <th className={`border-r ${border}`}></th>
                {dataIC && dataIC.map((istituto, index) =>
                    <th className={`${numColTable !== null ? ('w-1/' + numColTable) : ''}`}
                        key={index}>{istituto.descrizione2}</th>)}
            </tr>
            </thead>}

            <tbody>
            {/* NON CI SONO LE INFO */}
            {(!dataRiepilogo && dataIC) && <tr>
                <td className="text-red-600 font-semibold col-span-full text-center">Informazioni non presenti.</td>
            </tr>}

            {/* SE CI SONO LE INFO */}
            {(dataRiepilogo && dataIC) && <>
                <ICTableRow tdLabel="Esposizione" codiceValore="TE" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>{SaldoICRecord.getTipoEsposizione(item)}</td>)
                    }
                </ICTableRow>

                <ICTableRow tdLabel="Anni prec." codiceValore="RES_AP" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>{mostraValoreIC(item.codIstituto, index, 'RES_AP') && item.residuoAnnoPrec.toFixed(2)}</td>)}
                </ICTableRow>

                <ICTableRow tdLabel="Contrattuale" codiceValore="CONTR" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>{mostraValoreIC(item.codIstituto, index, 'CONTR') && item.contrattuale.toFixed(2)}</td>)}
                </ICTableRow>

                <ICTableRow tdLabel="Increm. mese" codiceValore="INC" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>{mostraValoreIC(item.codIstituto, index, 'INC') && item.incremento.toFixed(2)}</td>)}
                </ICTableRow>

                <ICTableRow tdLabel="Maturate" codiceValore="MAT" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>{mostraValoreIC(item.codIstituto, index, 'MAT') && item.maturati.toFixed(2)}</td>)}
                </ICTableRow>

                {/*CON SALDI ATTUALIZZATI (da presenze e\o da WF)*/}
                <ICTableRow tdLabel={showRow("RES_MESE") ? "Godute" : `Godute al ${SaldoICRecord.getPeriodo(dataRiepilogo[0])}`} codiceValore="GOD" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>

                            {mostraValoreIC(item.codIstituto, index, 'GOD') && <>
                                {/*Goduto "da paghe"*/}
                                {item.goduti.toFixed(2)}
                                {/*Goduto attualizzato "da presenze"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_P') && SaldoICRecord.isAttualizzato(item) &&
                                    <><br/><span className="text-orange-500 text-xs">*{SaldoICRecord.getGodutoAttualizzato(item).toFixed(2)}</span></>}
                                {/*Goduto attualizzato "da richieste WF"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_WF') && SaldoICRecord.isAttualizzatoWF(item) &&
                                    <><br/><span className="text-blue-500 text-xs">*{SaldoICRecord.getGodutoAttualizzatoWF(item).toFixed(2)}</span></>}
                            </>
                            }
                        </td>)}
                </ICTableRow>

                {/*CON SALDI ATTUALIZZATI (da presenze e\o da WF)*/}
                <ICTableRow tdLabel={`Res. al ${SaldoICRecord.getPeriodo(dataRiepilogo[0])}`} codiceValore="RES_MESE" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>

                            {mostraValoreIC(item.codIstituto, index, 'RES_MESE') && <>
                                {/*Residuo "da paghe"*/}
                                {item.residuoMese.toFixed(2)}
                                {/*Residuo attualizzato "da presenze"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_P') && SaldoICRecord.isAttualizzato(item) &&
                                    <><br/><span className="text-orange-500 text-xs">*{SaldoICRecord.getResiduoMeseAttualizzato(item).toFixed(2)}</span></>}
                                {/*Residuo attualizzato "da richieste WF"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_WF') && SaldoICRecord.isAttualizzatoWF(item) &&
                                    <><br/><span className="text-blue-500 text-xs">*{SaldoICRecord.getResiduoMeseAttualizzatoWF(item).toFixed(2)}</span></>}
                            </>}
                        </td>)}
                </ICTableRow>

                {/*CON SALDI ATTUALIZZATI (da presenze e\o da WF)*/}
                <ICTableRow tdLabel="Res. fine anno" codiceValore="RES_ANNO" dataIC={dataIC} page={page}>
                    {dataRiepilogo!.map((item, index) =>
                        <td key={index}>
                            {mostraValoreIC(item.codIstituto, index, 'RES_ANNO') && <>
                                {/*Saldo a fine anno "da paghe"*/}
                                {item.residuoAnno.toFixed(2)}
                                {/*Saldo a fine anno attualizzato "da presenze"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_P') && SaldoICRecord.isAttualizzato(item) &&
                                    <><br/><span className="text-orange-500 text-xs">*{SaldoICRecord.getResiduoAnnoAttualizzato(item).toFixed(2)}</span></>}
                                {/*Saldo a fine anno attualizzato "da richieste WF"*/}
                                {mostraValoreIC(item.codIstituto, index, 'ATT_WF') && SaldoICRecord.isAttualizzatoWF(item) &&
                                    <><br/><span className="text-blue-500 text-xs">*{SaldoICRecord.getResiduoAnnoAttualizzatoWF(item).toFixed(2)}</span></>}
                            </>}
                        </td>)}
                </ICTableRow>
            </>}
            </tbody>
            <>
                {/* PER Workflow, Dashboard, Presenze, ovvero dove è presente un periodo di riferimento (anno/mese) */}
                {showSaldiAttualizzati(SaldoICRecord.isAttualizzato, (page === 'Presenze' ? null : 'ATT_P')) && page !== 'monitorWF' &&
                    <caption className="caption-bottom text-orange-500 text-xs mt-1">
                        * Valori attualizzati al mese in gestione. Il residuo del mese &egrave; al netto dell'incremento mensile.
                    </caption>}

                {/* PER MONITOR */}
                {showSaldiAttualizzati(SaldoICRecord.isAttualizzato, 'ATT_P') && page === 'monitorWF' &&
                    <caption className="caption-bottom text-orange-500 text-xs mt-1">
                        * Valori attualizzati a {DateTime.now().toFormat('LLLL yyyy')}. Il residuo del mese &egrave; al netto dell'incremento mensile.
                    </caption>}

                {/* PER tutte le pagine, se attiva visualizzazione che tiene conto delle richieste inserite! */}
                {showSaldiAttualizzati(SaldoICRecord.isAttualizzatoWF, 'ATT_WF') &&
                    <caption className="caption-bottom text-blue-500 text-xs mt-1">
                        * Valori attualizzati a fine anno con le richieste di WF.
                    </caption>}
            </>
        </table>
    </>
}

export default RiepilogoIC;

const ICTableRow: React.FC<{ tdLabel: string, codiceValore: string, dataIC: IstitutoContrattualeRecord[], children: ReactNode, page: PageType | undefined }> =
    ({
         tdLabel, codiceValore, dataIC, children, page,
     }) => {
        /**
         * Funzione per nascondere la riga della tabella dei "campi da visualizzare"
         * @param campo stringa corrispondente al campo da visualizzare Es. 'RES_AP'
         */
        function showRow(campo: string) {
            if (dataIC) {
                const tipoCampo = dataIC!.map((item) => IstitutoContrattualeRecord.getFieldList(item))
                /*
                Cerco all'interno di TipoCampo, se c'è almeno un elemento che corrisponde a "ic".
                    Quindi restituisco true se ne trovo almeno 1, e mostro la riga della tabella.
                    Altrimenti false e non mostro la riga.
                */
                if (codiceValore === 'TE') {
                    return tipoCampo[0].some((arrVal) => campo === arrVal)
                } else {
                    for (let item of tipoCampo) {
                        if (item.some((arrVal) => campo === arrVal))
                            return true;
                    }
                }

            } else return false;
        }

        const tdLabelClass = `tableRiepiloghiLabelTD min-w-[85px] ${page === 'monitorWF' ? 'border-[#e5e7eb]' : ''}`;
        const trClass = `text-center align-top ${codiceValore === 'TE' ? 'font-light' : ''}`;

        return <>{showRow(codiceValore) &&
            <tr className={trClass}>
                <td className={tdLabelClass}>{tdLabel}</td>
                {children}
            </tr>
        }</>
    }